// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useDashboardApi } from '../../../store/api/hooks/dashboardApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import Button from '../../../components/elements/button';
import { downloadExcel } from '../../../_common/functions';
import toast from 'react-hot-toast';

// ≤30 days = Red, ≤90 days (3 months) = Yellow, >90 days = Green
function warrantyStatusCell(params: any) {
  const val = params.value;
  if (!val) return '';
  const styles: Record<string, { bg: string; color: string }> = {
    'Expired': { bg: '#fee2e2', color: '#dc2626' },
    'Expiring': { bg: '#fee2e2', color: '#dc2626' },
    'Expiring Soon': { bg: '#fefce8', color: '#ca8a04' },
    'Active': { bg: '#dcfce7', color: '#16a34a' },
  };
  const s = styles[val] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{val}</span>;
}

function expiryDateCell(params: any) {
  const row = params.data;
  if (!row || !row.warrantyEndDate) return '—';
  const date = new Date(row.warrantyEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const days = row.daysLeft;
  let color = '#16a34a';
  let fontWeight = 400;
  if (days < 0) { color = '#9ca3af'; }
  else if (days <= 30) { color = '#dc2626'; fontWeight = 600; }
  else if (days <= 90) { color = '#ca8a04'; }
  return <span style={{ color, fontWeight }}>{date}</span>;
}

function daysLeftCell(params: any) {
  const val = params.value;
  if (val == null) return '';
  let color = '#16a34a';
  if (val < 0) color = '#9ca3af';
  else if (val <= 30) color = '#dc2626';
  else if (val <= 90) color = '#ca8a04';
  return <span style={{ color, fontWeight: 600 }}>{val < 0 ? 'Expired' : val + 'd'}</span>;
}

function dateCell(params: any) {
  const val = params.value;
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const WarrantiesReport: React.FC = () => {
  const dashboardApi = useDashboardApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility) params.FacilityIDs = filterParams.facility;
    if (filterParams.assetGroup) params.AssetGroupIDs = filterParams.assetGroup;
    if (filterParams.department) params.DepartmentIDs = filterParams.department;
    if (filterParams.warrantyStatus) params.WarrantyStatus = filterParams.warrantyStatus;

    dashboardApi.getWarrantiesReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.summary?.totalWarranties || 0);
        setFilterOptions([
          { key: 'facility', label: 'Facility', options: (d?.facilities || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'assetGroup', label: 'Asset Group', options: (d?.assetGroups || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'department', label: 'Department', options: (d?.departments || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'warrantyStatus', label: 'Warranty Status', options: [
            { value: 'Expired', label: 'Expired' },
            { value: 'Expiring', label: 'Expiring (≤30d)' },
            { value: 'Expiring Soon', label: 'Expiring Soon (≤90d)' },
            { value: 'Active', label: 'Active' },
          ]},
        ]);
      },
      (error: any) => { console.error('Failed to fetch warranties:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>) => {
    setFilters(filterMap);
    setCurrentPage(1);
    fetchData(1, filterMap);
  };

  const handleExport = () => {
    const params: any = { PageSize: null, PageNumber: null };
    if (filters.facility) params.FacilityIDs = filters.facility;
    if (filters.assetGroup) params.AssetGroupIDs = filters.assetGroup;
    if (filters.department) params.DepartmentIDs = filters.department;
    if (filters.warrantyStatus) params.WarrantyStatus = filters.warrantyStatus;
    setIsExporting(true);
    dashboardApi.exportWarrantiesReportToXLS(
      params,
      (resp: any) => { downloadExcel(resp, 'warranties_report'); toast.success('Exported successfully!'); setIsExporting(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExporting(false); }
    );
  };

  const columnDefs = [
    { headerName: 'Asset Name', field: 'assetName', headerClass: 'bg-[#f0f0f0]', pinned: 'left', minWidth: 200, flex: 1, cellRenderer: titleCell, headerTooltip: 'Asset Name' },
    { headerName: 'Asset Code', field: 'assetCode', headerClass: 'bg-[#f0f0f0]', width: 160, cellRenderer: titleCell, headerTooltip: 'Asset Code' },
    { headerName: 'Facility', field: 'facilityName', headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Facility' },
    { headerName: 'Sub Facility', field: 'subFacilityName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Sub Facility' },
    { headerName: 'Asset Group', field: 'assetGroupName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Asset Group' },
    { headerName: 'Department', field: 'departmentName', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Department' },
    { headerName: 'Purchase Date', field: 'purchaseDate', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: dateCell, headerTooltip: 'Purchase Date' },
    { headerName: 'Warranty Start', field: 'warrantyStartDate', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: dateCell, headerTooltip: 'Warranty Start Date' },
    { headerName: 'Warranty Expiry', field: 'warrantyEndDate', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: expiryDateCell, headerTooltip: 'Warranty Expiry Date' },
    { headerName: 'Days Left', field: 'daysLeft', headerClass: 'bg-[#f0f0f0]', width: 100, type: 'rightAligned', cellRenderer: daysLeftCell, headerTooltip: 'Days Until Expiry' },
    { headerName: 'Status', field: 'warrantyStatus', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: warrantyStatusCell, headerTooltip: 'Warranty Status' },
    { headerName: 'Supplier', field: 'vendorName', headerClass: 'bg-[#f0f0f0]', width: 160, cellRenderer: titleCell, headerTooltip: 'Supplier/Vendor' },
  ];

  return (
    <Layout>
      <Header title="Warranties Report" back="/reports">
        <Header.Right>
          <Button theme='primary' onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} showDateRange={false} onApply={handleApplyFilter} />
        {summary && (
          <div className="bg-white rounded-lg mb-4 flex items-center gap-6 flex-wrap text-[13px]" style={{ padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <span style={{ color: '#111827', fontWeight: 600 }}>Total: {summary.totalWarranties}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#16a34a', fontWeight: 600 }}>Active: {summary.activeCount}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#ca8a04', fontWeight: 600 }}>Expiring Soon (≤90d): {summary.expiringSoonCount}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>Expiring (≤30d): {summary.expiringCount}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#9ca3af', fontWeight: 600 }}>Expired: {summary.expiredCount}</span>
          </div>
        )}
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </Layout>
  );
};

export default WarrantiesReport;
